import React from 'react';
import { CalendarClock } from 'lucide-react';

/**
 * Réglage facultatif de la clôture automatique des réponses convives, et de l'email d'avertissement envoyé
 * aux non-répondants avant l'échéance. Idée d'Emeni (2026-08-24) : garder le bouton « Clôturer » manuel,
 * mais pouvoir aussi programmer la fermeture pour ne pas avoir à y penser le jour dit.
 *
 * Des DATES seules, sans heure — « les réponses ferment le 28 août » se comprend mieux qu'un horaire à la
 * minute (décision d'Emeni). Le convive dispose de la journée entière du dernier jour : la fermeture
 * n'intervient que le lendemain matin, et l'email d'avertissement part le matin de la date choisie.
 */
export function ClotureProgrammeeCard({
    closeDate, prewarnDate, closeNotify, onCloseDateChange, onPrewarnDateChange, onCloseNotifyChange,
    onSave, saving, error, alreadyClosed, prewarningSentAt, savedCloseDate, savedPrewarnDate, savedCloseNotify,
}: {
    closeDate: string;
    prewarnDate: string;
    /** Envoyer l'email de clôture aux non-répondants au moment de la fermeture automatique. */
    closeNotify: boolean;
    onCloseDateChange: (v: string) => void;
    onPrewarnDateChange: (v: string) => void;
    onCloseNotifyChange: (v: boolean) => void;
    onSave: () => void;
    saving: boolean;
    /** Message de garde-fou (dates incohérentes) — bloque l'enregistrement tant qu'il est présent. */
    error: string;
    alreadyClosed: boolean;
    prewarningSentAt?: string | null;
    savedCloseDate: string;
    savedPrewarnDate: string;
    savedCloseNotify: boolean;
}) {
    const unchanged = closeDate === savedCloseDate && prewarnDate === savedPrewarnDate && closeNotify === savedCloseNotify;

    return (
        <div className="bg-surface-soft border border-border rounded-2xl p-4 space-y-3">
            <div>
                <h3 className="text-sm font-bold text-content flex items-center gap-2"><CalendarClock size={15} /> Clôture programmée</h3>
                <p className="text-[11px] text-content-muted mt-1">
                    Facultatif. Sans ces dates, rien ne change : vous clôturez à la main quand vous le souhaitez.
                </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-content">Dernier jour pour répondre</label>
                    <input type="date" value={closeDate} onChange={(e) => onCloseDateChange(e.target.value)}
                        disabled={alreadyClosed}
                        className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500 disabled:opacity-50" />
                    <p className="text-[10px] text-content-muted">
                        Les convives ont toute la journée de cette date ; les réponses ferment ensuite automatiquement.
                    </p>
                </div>
                <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-content">Prévenir les non-répondants le</label>
                    <input type="date" value={prewarnDate} onChange={(e) => onPrewarnDateChange(e.target.value)}
                        max={closeDate || undefined} disabled={alreadyClosed}
                        className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500 disabled:opacity-50" />
                    <p className="text-[10px] text-content-muted">
                        Un email part ce matin-là à ceux qui n'ont pas encore répondu, leur rappelant la date limite.
                    </p>
                </div>
            </div>

            {/* Sans cette case, la fermeture automatique se faisait en silence et le convive découvrait seul
                qu'il avait laissé passer le délai — alors que le bouton « Clôturer » manuel, lui, prévient.
                Cochée par défaut : c'est le comportement attendu quand on programme soi-même une date. */}
            <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={closeNotify} onChange={(e) => onCloseNotifyChange(e.target.checked)}
                    disabled={alreadyClosed}
                    className="mt-0.5 w-4 h-4 rounded border-border accent-brand-600 shrink-0 disabled:opacity-50" />
                <span className="text-[11px] text-content-muted">
                    <span className="font-bold text-content block">Prévenir les non-répondants de la fermeture</span>
                    À la clôture, ceux qui n'ont pas répondu reçoivent l'email « Clôture » — le même que le bouton
                    « Clôturer », personnalisable dans les réglages des emails convives.
                </span>
            </label>

            {alreadyClosed ? (
                <p className="text-[11px] font-semibold text-content-muted">Les réponses sont déjà closes — ces dates n'ont plus d'effet.</p>
            ) : error ? (
                <p className="text-[11px] font-semibold text-red-500">{error}</p>
            ) : prewarningSentAt && prewarnDate === savedPrewarnDate ? (
                <p className="text-[11px] text-content-muted">L'email d'avertissement a déjà été envoyé. Changer la date en fera partir un nouveau.</p>
            ) : null}

            <div className="flex justify-end">
                <button onClick={onSave} disabled={saving || !!error || unchanged || alreadyClosed}
                    className="px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-xs disabled:opacity-40">
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
            </div>
        </div>
    );
}
